import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, Box, Loader2, FileDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import ProductConfigurator from '../components/ProductConfigurator';

const Viewer3DLazy = lazy(() => import('../components/Viewer3D'));
const ErrorBoundary = lazy(() => import('../components/ErrorBoundary'));

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [customConfig, setCustomConfig] = useState({});
  const [displayPrice, setDisplayPrice] = useState(0);
  const [show3D, setShow3D] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product) {
      setDisplayPrice(product.promotional_price || product.price);
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Produto não encontrado');
      navigate('/catalogo');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (config) => {
    setCustomConfig(config);
  };

  const handlePriceChange = (newPrice) => {
    setDisplayPrice(newPrice);
  };

  // Check if customization is complete (if product requires it)
  const isConfigComplete = () => {
    if (!product?.is_customizable) return true;
    
    const hasFabrics = product.fabric_options?.length > 0;
    const hasSizes = product.size_options?.length > 0;
    
    if (hasFabrics && !customConfig.fabric) return false;
    if (hasSizes && !customConfig.size) return false;
    
    return true;
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Faça login para adicionar ao carrinho');
      navigate('/login');
      return;
    }

    // Check if customization is required and complete
    if (!isConfigComplete()) {
      toast.error('Complete a personalização do produto antes de adicionar ao carrinho');
      return;
    }

    try {
      // Pass customization data to cart
      await addToCart(product.id, quantity, {
        customConfig,
        customPrice: displayPrice
      });
      toast.success('Produto adicionado ao carrinho!');
    } catch (error) {
      toast.error('Erro ao adicionar ao carrinho');
    }
  };

  if (loading) {
    return (
      <div data-testid="loading-state" className="py-20 text-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div data-testid="product-detail-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        <button
          onClick={() => navigate('/catalogo')}
          data-testid="back-button"
          className="inline-flex items-center gap-2 mb-12 text-sm text-muted-foreground hover:text-primary transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Voltar ao Catálogo
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Images */}
          <div>
            <div className="aspect-[4/3] mb-6 overflow-hidden bg-neutral-50 relative group">
              <img
                src={product.images[selectedImage] || 'https://via.placeholder.com/600x450?text=Produto'}
                alt={product.name}
                data-testid="product-main-image"
                className="w-full h-full object-contain p-6"
              />
              {product.model_3d_url && (
                <button
                  onClick={() => setShow3D(true)}
                  data-testid="open-3d-viewer"
                  className="absolute bottom-4 right-4 flex items-center gap-2 bg-primary text-white px-5 py-3 uppercase tracking-widest text-[11px] hover:bg-black transition-all duration-500 shadow-lg"
                >
                  <Box className="w-4 h-4" strokeWidth={1.5} />
                  Ver em 3D
                </button>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((image, index) => (
                  <button
                    key={`${image}-${index}`}
                    onClick={() => setSelectedImage(index)}
                    data-testid={`thumbnail-${index}`}
                    className={`aspect-square overflow-hidden border-2 transition-all duration-300 ${
                      selectedImage === index ? 'border-primary' : 'border-transparent hover:border-border'
                    }`}
                  >
                    <img src={image} alt={`${product.name} ${index + 1}`} className="w-full h-full object-contain p-2 bg-neutral-50" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4">
              {product.category} • {product.type}
            </p>
            <h1 data-testid="product-name" className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-6">
              {product.name}
            </h1>
            <p data-testid="product-price" className="text-3xl font-light mb-8">
              <span>R$ {displayPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              {product.promotional_price && displayPrice === product.promotional_price && (
                <>
                  <span data-testid="product-original-price" className="text-sm text-muted-foreground ml-3 line-through font-normal">
                    R$ {(product.original_price || product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                  <span data-testid="product-discount-badge" className="ml-3 align-middle inline-block bg-red-600 text-white px-2 py-1 text-xs font-semibold">
                    -{Math.round(product.discount_percentage)}%
                  </span>
                </>
              )}
              {product.is_customizable && displayPrice !== product.price && displayPrice !== product.promotional_price && (
                <span className="block text-xs text-muted-foreground mt-2 font-normal">
                  A partir de R$ {product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              )}
            </p>

            <div className="mb-8 pb-8 border-b border-border">
              <p className="text-base font-light leading-relaxed text-muted-foreground">
                {product.description}
              </p>
            </div>

            {/* Product Configurator for customizable products */}
            {product.is_customizable && (
              <ProductConfigurator
                product={product}
                onConfigChange={handleConfigChange}
                onPriceChange={handlePriceChange}
              />
            )}

            <div className="mt-10 mb-8 pt-8 border-t border-border">
              <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4 block">
                Quantidade
              </label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="decrease-quantity"
                  className="w-12 h-12 border border-border hover:border-primary transition-colors duration-300"
                >
                  -
                </button>
                <span data-testid="quantity-display" className="text-lg font-medium w-12 text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="increase-quantity"
                  className="w-12 h-12 border border-border hover:border-primary transition-colors duration-300"
                >
                  +
                </button>
              </div>
            </div>

            <button
              onClick={handleAddToCart}
              data-testid="add-to-cart-button"
              disabled={!isConfigComplete()}
              className={`w-full flex items-center justify-center gap-3 px-8 py-4 uppercase tracking-widest text-xs transition-all duration-500 mb-4 ${
                isConfigComplete()
                  ? 'bg-primary text-white hover:bg-black'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
            >
              <ShoppingCart className="w-4 h-4" strokeWidth={1.5} />
              {isConfigComplete() ? 'Adicionar ao Carrinho' : 'Complete a Personalização'}
            </button>

            {product.stock > 0 && (
              <p className="text-sm text-muted-foreground text-center">
                {product.stock} unidades em estoque
              </p>
            )}

            {product.source_file_url && (
              <a
                href={
                  product.source_file_url.startsWith('http')
                    ? product.source_file_url
                    : `${process.env.REACT_APP_BACKEND_URL}${product.source_file_url}`
                }
                download
                data-testid="download-source-file"
                className="mt-6 w-full flex items-center justify-center gap-3 px-8 py-4 border border-primary text-primary uppercase tracking-widest text-xs hover:bg-primary hover:text-white transition-all duration-500"
              >
                <FileDown className="w-4 h-4" strokeWidth={1.5} />
                Baixar arquivo {(product.source_file_format || 'fonte').toUpperCase()}
                <span className="text-[10px] normal-case tracking-normal ml-1 opacity-70">
                  (para arquitetos)
                </span>
              </a>
            )}
          </div>
        </div>
      </div>

      {show3D && product?.model_3d_url && (
        <div
          data-testid="viewer-3d-modal"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 md:p-6"
          onClick={(e) => e.target === e.currentTarget && setShow3D(false)}
        >
          <div className="relative w-full h-full max-w-6xl max-h-[92vh] bg-neutral-900 shadow-2xl">
            <Suspense
              fallback={
                <div className="w-full h-full flex items-center justify-center text-white">
                  <Loader2 className="w-8 h-8 animate-spin" strokeWidth={1.5} />
                </div>
              }
            >
              <ErrorBoundary>
                <Viewer3DLazy
                  url={
                    product.model_3d_url.startsWith('http')
                      ? product.model_3d_url
                      : `${process.env.REACT_APP_BACKEND_URL}${product.model_3d_url}`
                  }
                  format={product.model_3d_format}
                  productId={product.id}
                  source="product"
                  onClose={() => setShow3D(false)}
                />
              </ErrorBoundary>
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;